import { useState } from "react";
import { Link } from "react-router-dom";
import { API } from "@/lib/api";
import { ArrowLeft, Mail } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      await fetch(`${API}/auth/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim() }),
      });
    } catch { /* silent — response is generic either way */ }
    finally {
      setSent(true);
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-neutral-50 px-4" data-testid="forgot-password-page">
      <div className="w-full max-w-sm">
        <Link to="/login" className="text-neutral-500 hover:text-black text-sm inline-flex items-center gap-1 mb-6 no-underline">
          <ArrowLeft className="w-4 h-4" /> Back to sign in
        </Link>
        {sent ? (
          <div data-testid="forgot-password-sent">
            <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mb-4">
              <Mail className="w-6 h-6 text-emerald-700" />
            </div>
            <h2 className="font-display text-2xl font-bold mb-3">Check your inbox</h2>
            <p className="text-sm text-neutral-600 leading-relaxed mb-4">
              If <strong>{email}</strong> matches an account, we've sent a password reset link. It expires in 30 minutes.
            </p>
            <p className="text-xs text-neutral-500">
              Nothing yet? Check spam, then double-check the email spelling and try again. For account-not-found situations we don't send an email — the response is identical for security reasons.
            </p>
          </div>
        ) : (
          <form onSubmit={submit}>
            <div className="overline mb-3">Reset password</div>
            <h2 className="font-display text-2xl font-bold mb-2 tracking-tight">Forgot your password?</h2>
            <p className="text-sm text-neutral-500 mb-6">Enter your email and we'll send you a one-time reset link.</p>
            <label className="block text-xs font-semibold tracking-widest uppercase text-neutral-500 mb-2">Email</label>
            <input
              type="email" required autoFocus autoComplete="email"
              data-testid="forgot-password-email"
              value={email} onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 border border-neutral-300 bg-white focus:outline-none focus:border-black mb-6 font-mono text-sm"
              placeholder="you@company.com"
            />
            <button data-testid="forgot-password-submit" type="submit" disabled={busy} className="lf-btn-primary w-full">
              {busy ? "Sending…" : "Email me a reset link →"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
